import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getComicById } from '@/mocks/comics';

const panelColors = [
  'bg-gradient-to-b from-rose-100 to-rose-50',
  'bg-gradient-to-b from-amber-50 to-white',
  'bg-gradient-to-b from-teal-50 to-white',
  'bg-gradient-to-b from-orange-50 to-white',
  'bg-gradient-to-b from-pink-50 to-white',
  'bg-gradient-to-b from-sky-50 to-white',
  'bg-gradient-to-b from-violet-50 to-white',
  'bg-gradient-to-b from-emerald-50 to-white',
  'bg-gradient-to-b from-yellow-50 to-white',
  'bg-gradient-to-b from-red-50 to-white',
  'bg-gradient-to-b from-indigo-50 to-white',
  'bg-gradient-to-b from-lime-50 to-white',
];

export default function ReadPage() {
  const { id, chapterNum } = useParams<{ id: string; chapterNum: string }>();
  const comic = id ? getComicById(id) : undefined;

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500">未找到该漫画</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const chapter = comic.chapters.find((ch) => ch.chapterNumber === Number(chapterNum));
  const currentNum = Number(chapterNum) || 1;
  const prevChapter = comic.chapters.find((ch) => ch.chapterNumber === currentNum - 1);
  const nextChapter = comic.chapters.find((ch) => ch.chapterNumber === currentNum + 1);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16 pb-14 bg-background-950">
        <div className="bg-background-950 text-background-100 border-b border-background-800">
          <div className="w-full max-w-4xl mx-auto px-4 py-3">
            <div className="flex items-center gap-4 text-xs">
              <Breadcrumb items={[
                { name: '首页', path: '/' },
                { name: comic.title, path: `/comic/${comic.id}` },
                { name: chapter?.title || `第${chapterNum}话` },
              ]} />
            </div>
          </div>
        </div>

        <div className="bg-background-950 border-b border-background-800 sticky top-16 z-40">
          <div className="w-full max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link
              to={`/comic/${comic.id}`}
              className="flex items-center gap-1 text-sm text-background-300 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回详情
            </Link>
            <h1 className="text-sm text-background-100 font-medium truncate max-w-[200px] md:max-w-[400px]">
              {comic.title} - {chapter?.title || `第${chapterNum}话`}
            </h1>
            <div className="flex items-center gap-3">
              {prevChapter ? (
                <Link
                  to={`/comic/${comic.id}/read/${prevChapter.chapterNumber}`}
                  className="flex items-center gap-1 text-sm text-background-300 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </Link>
              ) : (
                <span className="text-sm text-background-600 whitespace-nowrap">
                  <i className="ri-arrow-left-s-line"></i> 上一话
                </span>
              )}
              {nextChapter ? (
                <Link
                  to={`/comic/${comic.id}/read/${nextChapter.chapterNumber}`}
                  className="flex items-center gap-1 text-sm text-background-300 hover:text-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </Link>
              ) : (
                <span className="text-sm text-background-600 whitespace-nowrap">
                  下一话 <i className="ri-arrow-right-s-line"></i>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="w-full max-w-3xl mx-auto px-4 py-8">
          <div className="flex flex-col gap-4">
            {panelColors.map((color, i) => (
              <div key={i} className={`w-full rounded-lg overflow-hidden ${color} flex items-center justify-center`}
                style={{ minHeight: '400px' }}>
                <div className="text-center p-8">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-50/60 flex items-center justify-center">
                    <i className="ri-image-line text-2xl text-foreground-300"></i>
                  </div>
                  <p className="text-sm text-foreground-400 font-medium">
                    {comic.title} · 第{chapterNum}话
                  </p>
                  <p className="text-xs text-foreground-300 mt-1">
                    第 {i + 1} 页
                  </p>
                  <div className="mt-4 space-y-2 max-w-sm mx-auto">
                    <div className="h-2 bg-foreground-200/20 rounded-full w-3/4 mx-auto"></div>
                    <div className="h-2 bg-foreground-200/20 rounded-full w-1/2 mx-auto"></div>
                    <div className="h-2 bg-foreground-200/20 rounded-full w-2/3 mx-auto"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mt-10 pt-8 border-t border-background-800">
            {prevChapter ? (
              <Link
                to={`/comic/${comic.id}/read/${prevChapter.chapterNumber}`}
                className="flex items-center gap-2 px-5 py-2.5 bg-background-800 text-background-100 rounded-full text-sm hover:bg-background-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 上一话
              </Link>
            ) : (
              <div></div>
            )}
            <Link
              to={`/comic/${comic.id}`}
              className="px-5 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回目录
            </Link>
            {nextChapter ? (
              <Link
                to={`/comic/${comic.id}/read/${nextChapter.chapterNumber}`}
                className="flex items-center gap-2 px-5 py-2.5 bg-background-800 text-background-100 rounded-full text-sm hover:bg-background-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                下一话 <i className="ri-arrow-right-line"></i>
              </Link>
            ) : (
              <div></div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}